import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { getUserDocuments, deleteDocument } from "@/shared/db";
import { Button, CountryFlag, Input, Select } from "@/shared/ui";
import { ListPageSkeleton } from "../../../components/skeletons";
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  PencilIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  CalendarIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import {
  ROUTES,
  formatDate,
  generatePDF,
  downloadPDF,
  getCountryName,
} from "@/shared/core";
import { useCountries } from "@/hooks/useCountries";
import { useConfirm } from "../../../hooks/useConfirm";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trash2 } from "lucide-react";

interface Document {
  id: string;
  title: string;
  content: string;
  country: string;
  document_type: string;
  created_at: string;
  updated_at: string;
  status: string;
}

export const LawyerDocumentsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { countries } = useCountries();
  const { user } = useAuth();
  const { subscription } = usePlanLimits();
  const { confirm, Modal } = useConfirm();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [countryFilter, setCountryFilter] = useState<string>("all");

  useEffect(() => {
    if (user) {
      loadDocuments();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterDocuments();
  }, [documents, searchTerm, statusFilter, countryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const docs = await getUserDocuments(user.id);
      setDocuments(docs as Document[]);
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error(t("lawyer.documents.error"));
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = [...documents];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (doc) =>
          doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.document_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((doc) => doc.status === statusFilter);
    }

    // Filter by country
    if (countryFilter !== "all") {
      filtered = filtered.filter((doc) => doc.country === countryFilter);
    }

    setFilteredDocuments(filtered);
  };

  const handleDownload = (doc: Document) => {
    try {
      const pdf = generatePDF({
        title: doc.title,
        content: doc.content,
        country: getCountryName(doc.country) || doc.country,
        date: formatDate(doc.created_at),
        showBranding: subscription?.planType === "lawyer_free",
      });
      downloadPDF(pdf, doc.title);
      toast.success(t("lawyer.documents.downloadSuccess"));
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error(t("lawyer.documents.downloadError"));
    }
  };

  const handleEdit = (docId: string) => {
    navigate(`${ROUTES.DOCUMENT}/${docId}`);
  };

  const handleView = (docId: string) => {
    navigate(`${ROUTES.DOCUMENT}/${docId}`);
  };

  const handleDelete = async (docId: string, docTitle: string) => {
    await confirm({
      title: t("lawyer.documents.delete.title"),
      message: t("lawyer.documents.delete.confirm", { title: docTitle }),
      confirmText: t("common.delete"),
      cancelText: t("common.cancel"),
      variant: "danger",
      onConfirm: async () => {
        try {
          await deleteDocument(docId);
          setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
          toast.success(t("lawyer.documents.delete.success"));
        } catch (error) {
          console.error("Error deleting document:", error);
          toast.error(t("lawyer.documents.delete.error"));
        }
      },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "draft":
        return "bg-linear-to-r from-tiber-400 to-tiber-600 text-white";
      case "final":
        return "bg-linear-to-r from-atlantis-500 to-atlantis-700 text-white";
      case "archived":
        return "bg-linear-to-r from-tiber-600 to-tiber-800 text-white";
      default:
        return "bg-linear-to-r from-tiber-500 to-tiber-600 text-white";
    }
  };

  const getDocumentTypeColor = (type: string) => {
    const colors = [
      "from-tiber-400 to-tiber-600",
      "from-tiber-500 to-tiber-600",
      "from-atlantis-500 to-atlantis-700",
      "from-dorado to-dorado-dark",
      "from-tiber-500 to-tiber-700",
      "from-atlantis-400 to-atlantis-600",
    ];
    const index = type.length % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <ListPageSkeleton
        showStats={true}
        showFilters={true}
        showViewToggle={false}
        statsCount={4}
        className="bg-linear-to-br from-romance-50 via-white to-romance-100"
      />
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between mb-6"
          >
            <div>
              <h1 className="text-4xl font-bold text-tiber-900">
                {t("lawyer.documents.title")}
              </h1>
              <p className="mt-2 text-base text-tiber-700">
                {t("lawyer.documents.subtitle")}
              </p>
            </div>
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.GENERATE)}
              leftIcon={<PlusCircleIcon className="w-5 h-5" />}
            >
              {t("lawyer.documents.newDocument")}
            </Button>
          </motion.div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-linear-to-br from-tiber-500 to-tiber-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/90 text-sm font-medium mb-1">
                    {t("lawyer.documents.stats.total")}
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {documents.length}
                  </p>
                </div>
                <DocumentTextIcon className="w-12 h-12 text-white/80" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-linear-to-br from-tiber-400 to-tiber-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm font-medium mb-1">
                    {t("lawyer.documents.stats.drafts")}
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {documents.filter((d) => d.status === "draft").length}
                  </p>
                </div>
                <DocumentDuplicateIcon className="w-12 h-12 text-white/80" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-linear-to-br from-atlantis-500 to-atlantis-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/90 text-sm font-medium mb-1">
                    {t("lawyer.documents.stats.final")}
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {documents.filter((d) => d.status === "final").length}
                  </p>
                </div>
                <DocumentTextIcon className="w-12 h-12 text-white/80" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-linear-to-br from-dorado to-dorado-dark rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/90 text-sm font-medium mb-1">
                    {t("lawyer.documents.stats.thisMonth")}
                  </p>
                  <p className="text-3xl font-bold text-white">
                    {
                      documents.filter(
                        (d) =>
                          new Date(d.created_at).getMonth() ===
                            new Date().getMonth() &&
                          new Date(d.created_at).getFullYear() ===
                            new Date().getFullYear()
                      ).length
                    }
                  </p>
                </div>
                <CalendarIcon className="w-12 h-12 text-white/80" />
              </div>
            </motion.div>
          </div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-tiber-600"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Input
                  type="text"
                  placeholder={t("lawyer.documents.search.placeholder")}
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  leftIcon={
                    <MagnifyingGlassIcon className="w-5 h-5 text-tiber-900" />
                  }
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <FunnelIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-tiber-900" />
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-12 pr-4 py-3"
                  options={[
                    {
                      value: "all",
                      label: t("lawyer.documents.filters.status.all"),
                    },
                    {
                      value: "draft",
                      label: t("lawyer.documents.filters.status.draft"),
                    },
                    {
                      value: "final",
                      label: t("lawyer.documents.filters.status.final"),
                    },
                    {
                      value: "archived",
                      label: t("lawyer.documents.filters.status.archived"),
                    },
                  ]}
                />
              </div>

              {/* Country Filter */}
              <div className="relative">
                <GlobeAltIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-tiber-900" />
                <Select
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="w-full pl-12 pr-4 py-3"
                  options={[
                    {
                      value: "all",
                      label: t("lawyer.documents.filters.country.all"),
                    },
                    ...countries.map((c) => ({
                      value: c.code,
                      label: i18n.language === "es" ? c.nameEs : c.nameEn,
                    })),
                  ]}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Documents List */}
        {filteredDocuments.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div>
              <DocumentTextIcon className="w-20 h-20 text-gray-300 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-tiber-900 mb-3">
                {t("lawyer.documents.empty.title")}
              </h3>
              <p className="text-tiber-700 mb-8 max-w-md mx-auto">
                {searchTerm || statusFilter !== "all" || countryFilter !== "all"
                  ? t("lawyer.documents.empty.filteredDescription")
                  : t("lawyer.documents.empty.description")}
              </p>
              {!searchTerm &&
                statusFilter === "all" &&
                countryFilter === "all" && (
                  <Button
                    variant="primary"
                    onClick={() => navigate(ROUTES.GENERATE)}
                    leftIcon={<PlusCircleIcon className="w-5 h-5" />}
                    title={t("lawyer.documents.empty.createFirst")}
                  />
                )}
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredDocuments.map((doc, index) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group bg-white rounded-xl p-6 shadow-lg border border-tiber-600 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Card Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className={`p-3 rounded-lg bg-linear-to-br ${getDocumentTypeColor(doc.document_type)}`}
                      >
                        <DocumentTextIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-tiber-900 truncate group-hover:text-tiber-400 transition-colors">
                          {doc.title}
                        </h3>
                        <p className="text-sm text-tiber-600 capitalize mt-1">
                          {doc.document_type}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getStatusColor(
                        doc.status
                      )}`}
                    >
                      {t(`lawyer.documents.status.${doc.status}`)}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="mb-4">
                  <div className="flex items-center gap-4 text-sm text-tiber-600 mb-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <CountryFlag
                        countryCode={doc.country}
                        size="1.2em"
                        title={getCountryName(doc.country)}
                      />
                      <span>{getCountryName(doc.country)}</span>
                    </div>
                    <span className="text-tiber-600">•</span>
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{formatDate(doc.created_at)}</span>
                    </div>
                  </div>
                  <p className="text-sm text-tiber-600 line-clamp-3 leading-relaxed">
                    {doc.content.substring(0, 150)}
                    {doc.content.length > 150 && "..."}
                  </p>
                </div>

                {/* Card Footer - Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-tiber-600">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleView(doc.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-linear-to-r from-tiber-50 to-tiber-500/10 text-tiber-800 rounded-lg hover:from-tiber-100 hover:to-tiber-500/20 transition-all font-medium text-sm shadow-sm hover:shadow"
                      title={t("lawyer.documents.actions.view")}
                    >
                      <EyeIcon className="w-4 h-4" />
                      <span>{t("lawyer.documents.actions.view")}</span>
                    </button>
                    <button
                      onClick={() => handleEdit(doc.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-linear-to-r from-tiber-50 to-tiber-500/10 text-tiber-800 rounded-lg hover:from-tiber-100 hover:to-tiber-500/20 transition-all font-medium text-sm shadow-sm hover:shadow"
                      title={t("lawyer.documents.actions.edit")}
                    >
                      <PencilIcon className="w-4 h-4" />
                      <span>{t("lawyer.documents.actions.edit")}</span>
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id, doc.title)}
                      className="flex items-center gap-2 px-4 py-2 bg-linear-to-r from-tiber-50 to-atlantis-50/50 text-tiber-800 rounded-lg hover:from-tiber-100 hover:to-atlantis-100 transition-all font-medium text-sm shadow-sm hover:shadow"
                      title={t("lawyer.documents.actions.delete")}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>{t("lawyer.documents.actions.delete")}</span>
                    </button>
                  </div>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 bg-linear-to-r from-atlantis-50 to-tiber-50 text-tiber-800 rounded-lg hover:from-atlantis-100 hover:to-tiber-100 transition-all shadow-sm hover:shadow"
                    title={t("lawyer.documents.actions.download")}
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        {Modal}
      </div>
    </div>
  );
};
